
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { PlusCircle, CreditCard, Trash2, Edit, Eye, MoreHorizontal } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import { ContaBancaria } from "@/types/financeiro";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getSafeNumber } from "../resumo/utils/numberUtils";

interface ContasBancariasListaProps {
  onAddConta: () => void;
}

// Definição explícita do tipo dos dados do banco
type ContaBancariaDB = {
  id: string | null;
  banco: string | null;
  agencia: string | null;
  numero: string | null;
  descricao: string | null;
  saldo_inicial: number | null;
  saldo_atual: number | null;
  ativo: boolean | null;
}

const ContasBancariasLista: React.FC<ContasBancariasListaProps> = ({ onAddConta }) => {
  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Load saved accounts from Supabase on component mount
  useEffect(() => {
    const loadContas = async () => {
      setIsLoading(true);
      try {
        if (!user) return;
        
        const { data, error } = await supabase
          .from('contas_bancarias')
          .select('*')
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        if (data) {
          // Create an array to store transformed accounts
          const contasTransformed: ContaBancaria[] = [];
          
          // Process each item individually using a for loop instead of map to avoid deep type instantiation
          for (let i = 0; i < data.length; i++) {
            const conta = data[i] as ContaBancariaDB;
            contasTransformed.push({
              id: conta.id || '',
              banco: conta.banco || '',
              agencia: conta.agencia || '',
              numero: conta.numero || '',
              descricao: conta.descricao || '',
              saldoInicial: getSafeNumber(conta.saldo_inicial),
              saldoAtual: getSafeNumber(conta.saldo_atual),
              ativo: Boolean(conta.ativo)
            });
          }
          
          setContas(contasTransformed);
        }
      } catch (error) {
        console.error("Erro ao carregar contas bancárias:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar as contas bancárias.",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadContas();
  }, [toast, user]);

  const handleExcluirConta = async (id: string) => {
    try {
      if (!user) return;
      
      const { error } = await supabase
        .from('contas_bancarias')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Update local state to reflect the deletion
      setContas(prevContas => prevContas.filter(conta => conta.id !== id));
      
      toast({
        title: "Conta removida",
        description: "A conta bancária foi removida com sucesso",
        duration: 3000,
      });
    } catch (error) {
      console.error("Erro ao excluir conta:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir a conta bancária.",
        duration: 3000,
      });
    }
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">Carregando contas bancárias...</p>
        </div>
      ) : contas.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Banco</TableHead>
              <TableHead className="text-xs">Agência</TableHead>
              <TableHead className="text-xs">Conta</TableHead>
              <TableHead className="text-xs">Descrição</TableHead>
              <TableHead className="text-right text-xs">Saldo Atual</TableHead>
              <TableHead className="w-[100px] text-xs">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contas.map((conta) => (
              <TableRow key={conta.id}>
                <TableCell className="font-medium text-xs">{conta.banco}</TableCell>
                <TableCell className="text-xs">{conta.agencia}</TableCell>
                <TableCell className="text-xs">{conta.numero}</TableCell>
                <TableCell className="text-xs">{conta.descricao}</TableCell>
                <TableCell className="text-right text-xs">
                  {conta.saldoAtual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Abrir menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="text-xs">
                        <Eye className="mr-2 h-4 w-4" /> Ver extrato
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-xs">
                        <Edit className="mr-2 h-4 w-4" /> Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-600 text-xs"
                        onClick={() => handleExcluirConta(conta.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Card className="flex items-center justify-center p-10 border-2 border-dashed border-financeiro-border-light rounded-lg text-financeiro-text-light">
          <div className="text-center">
            <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="mb-2 text-xs">Nenhuma conta bancária cadastrada</p>
            <p className="text-xs mb-4">Adicione suas contas bancárias para gerenciar seu fluxo de caixa</p>
            <Button 
              onClick={onAddConta}
              className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark text-xs"
            >
              <PlusCircle className="h-4 w-4 mr-2" /> Adicionar nova conta
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ContasBancariasLista;
